import { IonLoading, IonToast } from '@ionic/react';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { CharacterShow } from '../components/characters';
import { EXCEPTIONS } from '../constants';
import {
  DeviceContext,
  DeviceContextModel,
  TranslationContext,
  UserContext,
} from '../contexts';
import { analyticsHelper, authenticationHelper } from '../helpers';
import { webAnalyticsHelper } from '../helpers/webAnalytics';
import { APIError } from '../resource/APIError';
import { authResource, routerResource } from '../resource';
import './characterPage.scss';
import './chatIntro.scss';

export const CharacterPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const { translate } = useContext(TranslationContext);
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(
    translate('global.serverError'),
  );
  const device = useContext(DeviceContext) as DeviceContextModel;
  const { setUserInfos } = useContext(UserContext);
  const search = useLocation().search;
  const token = new URLSearchParams(search).get('token');

  // Map character codes to theme prefixes
  const characterThemeMap: { [key: string]: string } = {
    blaen: 'bla',
    agaen: 'aga',
  };

  const characterCode = code!;
  const themePrefix = characterThemeMap[characterCode] || 'bla';
  const characterName = characterCode === 'blaen' ? 'blanche' : 'agatha';

  useEffect(() => {
    const init = async () => {
      await registerOrSignIn();
    };
    init();
  }, []);

  const signin = async () => {
    try {
      const response = await authResource.create({
        identification: device.uuid,
        password: process.env.REACT_APP_DEFAULT_DEVICE_AUTH_PASSWORD,
      });
      setUserInfos(response);
      analyticsHelper.setUser(response['@id']);
      analyticsHelper.logEvent('login', {
        Device: device.model,
        Version: device.appVersion,
        OS: device.operatingSystem,
      });
      webAnalyticsHelper.setUser(response['@id']);
      webAnalyticsHelper.logEvent('login', {
        Device: device.model,
        Version: device.appVersion,
        OS: device.operatingSystem,
      });
    } catch (error: unknown) {
      const err = error as Error;
      if (err.message === EXCEPTIONS.INVALID_CREDENTIALS) {
        authenticationHelper.unsetDeviceIdUsed();
      } else {
        setDisplayError(true);
        throw error;
      }
    }
  };

  const registerToken = async () => {
    try {
      const response = await routerResource.create({
        token,
        deviceIdentifier: device.uuid,
      });
      setUserInfos(response);
    } catch (error: unknown) {
      const err = error as APIError;
      if (err.code === 401 && err.redirectUrl) {
        setErrorMessage(translate('global.tokenExpired'));
        setDisplayError(true);
        setLoading(false);
        setTimeout(() => {
          window.location.href = err.redirectUrl!;
        }, 4000);
        return;
      }
      if (err.message === EXCEPTIONS.DEVICE_ID_ALREADY_USED) {
        setErrorMessage(translate('global.deviceIdError'));
      }
      setDisplayError(true);
      throw error;
    }
    setLoading(false);
    authenticationHelper.setDeviceIdUsed();
  };

  const registerOrSignIn = async () => {
    setLoading(true);
    setDisplayError(false);
    if (token) {
      try {
        const isSigningUp = !authenticationHelper.isDeviceIdUsed();
        await registerToken();
        analyticsHelper.logEvent(isSigningUp ? 'sign_up' : 'recharge_balance', {
          Device: device.model,
          Version: device.appVersion,
          OS: device.operatingSystem,
        });
        webAnalyticsHelper.logEvent(
          isSigningUp ? 'sign_up' : 'recharge_balance',
          {
            Device: device.model,
            Version: device.appVersion,
            OS: device.operatingSystem,
          },
        );
      } catch (error: unknown) {
        const err = error as Error;
        if (err.message === EXCEPTIONS.EMAIL_ALREADY_USED) {
          setDisplayError(true);
          setLoading(false);

          throw error;
        }
        if (err.message !== EXCEPTIONS.DEVICE_ID_ALREADY_USED) {
          setDisplayError(true);
          setLoading(false);

          throw error;
        }
      }
    } else {
      await signin();
    }
    setLoading(false);
  };

  return (
    <div className={`${themePrefix}-theme`}>
      <div className="left-col">
        <img
          src={`/assets/img/${themePrefix}/i18n/en-US/left-${characterName}.${
            themePrefix === 'aga' ? 'webp' : 'png'
          }`}
          alt=""
          className={`left-${characterName}`}
        />
        {/* Optionally add logo/text here if needed */}
      </div>
      <div className="right-col">
        <div className="chat-intro">
          <CharacterShow code={code!} />
          <IonLoading isOpen={loading} message="Please wait..." />
          <IonToast
            isOpen={displayError}
            message={errorMessage}
            position="bottom"
          />
        </div>
      </div>
    </div>
  );
};
